import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getExpenseCategories, createExpenseCategory, updateExpenseCategory, deleteExpenseCategory, USE_MOCK_DATA } from '../services/api';
import { mockApartments, mockExpenseCategories } from '../services/mockData';

export default function ExpenseCategories() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    if (USE_MOCK_DATA) {
      const userApartments = [mockApartments[0]];
      setApartments(userApartments);
      setSelectedApartment(userApartments[0].id);
      return;
    }
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    if (USE_MOCK_DATA) {
      setCategories(mockExpenseCategories);
      return;
    }
    getExpenseCategories(selectedApartment).then(res => setCategories(res.data)).catch(() => {});
  }, [selectedApartment]);

  const loadCategories = () => {
    if (USE_MOCK_DATA) return;
    getExpenseCategories(selectedApartment).then(res => setCategories(res.data)).catch(() => {});
  };

  const handleSubmit = async () => {
    if (USE_MOCK_DATA) {
      if (editing) {
        setCategories(prev => prev.map(c => c.id === editing.id ? { ...c, name: form.name, description: form.description } : c));
      } else {
        setCategories(prev => [...prev, { id: Date.now(), name: form.name, description: form.description, apartmentId: selectedApartment, createdAt: new Date().toISOString() }]);
      }
    } else {
      if (editing) {
        await updateExpenseCategory(editing.id, { name: form.name, description: form.description });
      } else {
        await createExpenseCategory({ name: form.name, description: form.description, apartmentId: selectedApartment });
      }
      loadCategories();
    }
    setOpen(false);
    setEditing(null);
    setForm({ name: '', description: '' });
  };

  const handleEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, description: cat.description || '' });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this category?')) {
      if (USE_MOCK_DATA) {
        setCategories(prev => prev.filter(c => c.id !== id));
      } else {
        await deleteExpenseCategory(id);
        loadCategories();
      }
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Expense Categories</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Apartment</InputLabel>
            <Select value={selectedApartment} label="Apartment" onChange={(e) => setSelectedApartment(e.target.value)}>
              {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          {isSuperAdmin() && (
            <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({ name: '', description: '' }); setOpen(true); }}>
              Add Category
            </Button>
          )}
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created</TableCell>
              {isSuperAdmin() && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map(cat => (
              <TableRow key={cat.id}>
                <TableCell>{cat.name}</TableCell>
                <TableCell>{cat.description}</TableCell>
                <TableCell>{new Date(cat.createdAt).toLocaleDateString()}</TableCell>
                {isSuperAdmin() && (
                  <TableCell>
                    <IconButton onClick={() => handleEdit(cat)}><Edit /></IconButton>
                    <IconButton onClick={() => handleDelete(cat.id)} color="error"><Delete /></IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField fullWidth label="Description" margin="normal" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
